import React, { useState, useEffect } from 'react';
import { Download, Copy, Check, FileJson, FileCode, X } from 'lucide-react';

/**
 * Modal for exporting contracts in different formats
 */
const ContractExportModal = ({ contract, isOpen, onClose, onExport, addToast }) => {
    const [selectedFormat, setSelectedFormat] = useState('full');
    const [filename, setFilename] = useState('');
    const [copying, setCopying] = useState(false);
    const [copied, setCopied] = useState(false);

    // Reset filename on open
    useEffect(() => {
        if (isOpen && contract) {
            const now = new Date();
            const timestamp = now.toISOString().slice(0, 16).replace('T', '_').replace(':', '');
            const sanitizedName = contract.name.replace(/[^a-zA-Z0-9-_]/g, '_').toLowerCase();
            setFilename(`${sanitizedName}_${timestamp}.json`);
        }
    }, [isOpen, contract]);

    if (!isOpen || !contract) return null;

    const formats = [
        {
            id: 'full',
            name: 'JSON with Metadata',
            description: 'Includes contract name, export date, field count, and tags',
            icon: FileJson,
            bestFor: 'Documentation, sharing with partners'
        },
        {
            id: 'raw',
            name: 'Raw Schema',
            description: 'Just the schema content, nothing extra',
            icon: FileCode,
            bestFor: 'Direct integration, validation'
        }
    ];

    const handleDownload = async () => {
        try {
            await onExport(contract.id, selectedFormat, filename);
            addToast?.('Contract exported successfully', 'success');
            onClose();
        } catch (error) {
            addToast?.('Failed to export contract', 'error');
        }
    };

    const handleCopy = async () => {
        setCopying(true);
        try {
            const content = selectedFormat === 'raw'
                ? JSON.stringify(contract.schema_content, null, 2)
                : JSON.stringify({
                    contractName: contract.name,
                    exportedAt: new Date().toISOString(),
                    description: contract.description,
                    schema: contract.schema_content,
                    metadata: {
                        id: contract.id,
                        tags: contract.tags || [],
                        createdAt: contract.created_at,
                        updatedAt: contract.updated_at
                    }
                }, null, 2);

            await navigator.clipboard.writeText(content);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            addToast?.('Copied to clipboard', 'success');
        } catch (error) {
            addToast?.('Failed to copy to clipboard', 'error');
        } finally {
            setCopying(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-[#252526] rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#3c3c3c]">
                    <div>
                        <h2 className="text-lg font-semibold text-white">
                            Export Contract
                        </h2>
                        <p className="text-sm text-[#808080] mt-0.5">
                            {contract.name}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-[#3c3c3c] rounded-lg transition-colors"
                    >
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                {/* Format Selection */}
                <div className="p-6 space-y-4">
                    <p className="text-sm font-medium text-[#cccccc]">
                        Choose export format:
                    </p>

                    <div className="space-y-3">
                        {formats.map((format) => {
                            const Icon = format.icon;
                            const isSelected = selectedFormat === format.id;

                            return (
                                <button
                                    key={format.id}
                                    onClick={() => setSelectedFormat(format.id)}
                                    className={`w-full text-left p-4 rounded-xl border-2 transition-all ${isSelected
                                        ? 'border-blue-500 bg-blue-900/20'
                                        : 'border-[#3c3c3c] hover:border-gray-600'
                                        }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`p-2 rounded-lg ${isSelected
                                            ? 'bg-blue-500 text-white'
                                            : 'bg-[#3c3c3c] text-[#808080]'
                                            }`}>
                                            <Icon size={20} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="font-medium text-white">
                                                {format.name}
                                            </div>
                                            <div className="text-sm text-[#808080] mt-0.5">
                                                {format.description}
                                            </div>
                                        </div>
                                        {isSelected && (
                                            <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                                                <Check size={14} className="text-white" />
                                            </div>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Filename Input */}
                    <div>
                        <label className="block text-sm font-medium text-[#cccccc] mb-2">
                            Filename
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={filename}
                                onChange={(e) => setFilename(e.target.value)}
                                className="w-full px-3 py-2 pr-10 bg-[#1e1e1e] text-[#cccccc] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="contract_export.json"
                            />
                            {filename && (
                                <button
                                    onClick={() => setFilename('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                                    title="Clear filename"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 px-6 py-4 bg-[#1e1e1e]/50 border-t border-[#3c3c3c]">
                    <button
                        onClick={handleDownload}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                    >
                        <Download size={18} />
                        Download
                    </button>
                    <button
                        onClick={handleCopy}
                        disabled={copying}
                        className={`flex items-center justify-center gap-2 px-4 py-2.5 font-medium rounded-lg transition-colors ${copied
                            ? 'bg-green-900/30 text-green-400'
                            : 'bg-[#3c3c3c] text-[#cccccc] hover:bg-[#4c4c4c]'
                            }`}
                    >
                        {copied ? <Check size={18} /> : <Copy size={18} />}
                        {copied ? 'Copied!' : 'Copy'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContractExportModal;
